import React, { useEffect } from 'react';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  ClosingCta,
  DEEP,
  Eyebrow,
  GREEN,
  HERO_IMAGE,
  HeroTitle,
  Highlight,
  INK,
  LINE,
  LINE_ON_DARK,
  Lede,
  MOCK_SHADOW,
  MUTED,
  MarketingPage,
  SectionTitle,
  SplitHeading,
  primaryButtonSx,
  secondaryButtonSx,
} from '../components/marketing/MarketingKit';

const PRINCIPLES: Array<{ title: string; body: string }> = [
  {
    title: 'Fees only when you get paid',
    body: 'No forced subscriptions or paid tiers. Providers pay a small platform fee on completed jobs, so a slow or rainy week costs nothing.',
  },
  {
    title: 'Your customers stay yours',
    body: 'Customers you bring to LawnHub or win through it are your relationships. We give you better tools to serve them — not a toll to keep reaching them.',
  },
  {
    title: 'Tools, not just leads',
    body: 'CRM, routes, a crew app, payments, and financial reporting come with every provider account, whether you use the marketplace or not.',
  },
  {
    title: 'Plain answers',
    body: 'Clear fees, clear verification rules, and a straight answer when something goes wrong. If it affects your payout, you can see it.',
  },
];

const WONT_DO: Array<{ title: string; body: string }> = [
  {
    title: 'No rent on your own customers',
    body: 'Customers you bring onto LawnHub are 1% on completed jobs — never a monthly fee just to keep serving them.',
  },
  {
    title: 'No fees hidden in your payout',
    body: 'Every charge, platform fee, and payout is itemized. Card processing is paid by the customer, not taken from you.',
  },
  {
    title: 'No rate card telling you what to charge',
    body: 'You price your own services and properties. We give you the numbers to price well, not a rate card.',
  },
];

const AUDIENCES: Array<{ eyebrow: string; title: string; body: string; to: string; cta: string }> = [
  {
    eyebrow: 'For lawn care businesses',
    title: 'Run the whole operation in one place.',
    body: 'Customers, agreements, routes, crews, payments, forecasting, and profit per property — with no monthly subscription.',
    to: '/provider-benefits',
    cta: 'See what’s included',
  },
  {
    eyebrow: 'For homeowners',
    title: 'Hire lawn care without the phone tag.',
    body: 'Compare bids from insurance-verified providers, set up service for a parent’s home, and pay automatically after each visit.',
    to: '/customer-benefits',
    cta: 'How it works for homeowners',
  },
];

const AboutUsPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    navigate('/get-started');
  };

  return (
    <>
      <SEO
        title="About LawnHub | Lawn Care Business Software"
        description="LawnHub helps lawn care and snow removal businesses run operations nationwide — CRM, scheduling, crews, and payments — with fair fees and no forced subscriptions. Built for providers who take their business seriously."
        keywords="about LawnHub, lawn care business software, lawn care CRM, provider-friendly lawn care platform, snow removal software, landscaping business platform"
        url="/about-us"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About LawnHub',
          description: 'LawnHub helps lawn care and snow removal businesses manage operations and grow nationwide.',
          url: 'https://www.illinoislawnhub.com/about-us',
          mainEntity: {
            '@type': 'Organization',
            name: 'LawnHub',
            alternateName: 'Illinois LawnHub',
            url: 'https://www.illinoislawnhub.com',
            logo: 'https://www.illinoislawnhub.com/logo512.png',
            areaServed: {
              '@type': 'Country',
              name: 'United States',
            },
            knowsAbout: [
              'Lawn Care Business Software',
              'Lawn Care CRM',
              'Route Optimization',
              'Snow Removal Operations',
              'Crew Management',
            ],
          },
        }}
      />

      <MarketingPage>
        {/* Hero */}
        <Box component="section" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Eyebrow>About LawnHub</Eyebrow>
                <HeroTitle>
                  Built for the people who <Highlight>do the work.</Highlight>
                </HeroTitle>
                <Lede maxWidth={520}>
                  LawnHub gives lawn care and snow removal businesses the tools to run their operation and reach new
                  customers — with fair fees, no forced subscriptions, and customer relationships that stay theirs.
                </Lede>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                  <Button component={RouterLink} to="/provider-benefits" variant="contained" size="large" endIcon={<ArrowForward />} sx={primaryButtonSx}>
                    LawnHub for providers
                  </Button>
                  <Button component={RouterLink} to="/customer-benefits" variant="outlined" size="large" sx={secondaryButtonSx}>
                    LawnHub for homeowners
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', pb: { xs: 0, md: 6 } }}>
                  <Box
                    role="img"
                    aria-label="A freshly mowed lawn in front of a house"
                    sx={{
                      height: { xs: 260, sm: 340, md: 420 },
                      borderRadius: 4,
                      backgroundImage: `url("${HERO_IMAGE}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: '35% center',
                    }}
                  />
                  <Box
                    sx={{
                      position: { xs: 'relative', md: 'absolute' },
                      left: { md: -32 },
                      bottom: { md: 0 },
                      mt: { xs: -5, md: 0 },
                      mx: { xs: 2, md: 0 },
                      maxWidth: 340,
                      bgcolor: DEEP,
                      color: '#fff',
                      borderRadius: 3,
                      p: 3,
                      boxShadow: MOCK_SHADOW,
                    }}
                  >
                    <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.5, fontWeight: 600 }}>
                      “Success should be accessible to everyone willing to put in the work.”
                    </Typography>
                    <Typography sx={{ mt: 1.5, fontSize: '0.8rem', color: '#b9d8bd', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Founder, LawnHub
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Story */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}` }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 3, md: 10 }}>
              <Grid item xs={12} md={5}>
                <Eyebrow>Our story</Eyebrow>
                <SectionTitle>Started by a teacher and coach, not a corporation.</SectionTitle>
              </Grid>
              <Grid item xs={12} md={7}>
                <Stack spacing={2.5} sx={{ color: INK, fontSize: { xs: '1.02rem', md: '1.1rem' }, lineHeight: 1.75, maxWidth: 640 }}>
                  <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
                    I’m a business and technology teacher, and I’ve spent years coaching students and athletes. That work
                    runs on a few simple things — effort, preparation, and fairness. I kept seeing local business owners
                    get the opposite from the platforms they depended on.
                  </Typography>
                  <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
                    Lawn care owners were paying monthly subscriptions whether it was a busy week or a rainy one, paying
                    steep fees for leads, and using tools that treated their customers as the platform’s, not theirs. So I
                    set out to build something different: one place to run the business, with fees that only apply when
                    the work gets done.
                  </Typography>
                  <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
                    LawnHub isn’t a faceless corporation. Provider feedback decides what we build next, and the people
                    who use it every day are the ones we answer to.
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: MUTED, letterSpacing: '0.04em' }}>
                    — Founder, LawnHub
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Principles */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}` }}>
          <Container maxWidth="lg">
            <SplitHeading
              eyebrow="How we operate"
              title="Four promises to providers."
              body="The standards behind every feature we build and every fee we charge."
            />
            <Grid container columnSpacing={{ xs: 0, md: 8 }}>
              {PRINCIPLES.map((p, i) => (
                <Grid item xs={12} md={6} key={p.title}>
                  <Box sx={{ display: 'flex', gap: { xs: 2.5, md: 3.5 }, py: { xs: 3, md: 4 }, borderTop: `1px solid ${LINE}` }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        fontWeight: 800,
                        color: GREEN,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        minWidth: { xs: 44, md: 56 },
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </Typography>
                    <Box>
                      <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.2rem', color: INK, mb: 1 }}>
                        {p.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.975rem', lineHeight: 1.65, color: MUTED }}>{p.body}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* What we won't do */}
        <Box component="section" sx={{ bgcolor: DEEP, color: '#fff', py: { xs: 8, md: 11 } }}>
          <Container maxWidth="lg">
            <SplitHeading
              light
              eyebrow="Lines we don’t cross"
              title="What we won’t do."
              body="We’ve watched too many good operators get squeezed by the platforms they rely on. LawnHub is built to be the opposite."
            />
            <Grid container sx={{ borderTop: `1px solid ${LINE_ON_DARK}` }}>
              {WONT_DO.map((w, i) => (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={w.title}
                  sx={{
                    py: { xs: 3, md: 4 },
                    pr: { md: 4 },
                    pl: { md: i === 0 ? 0 : 4 },
                    borderLeft: { md: i === 0 ? 'none' : `1px solid ${LINE_ON_DARK}` },
                    borderBottom: { xs: `1px solid ${LINE_ON_DARK}`, md: 'none' },
                  }}
                >
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
                    {w.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.72)' }}>{w.body}</Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Who it's for */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 } }}>
          <Container maxWidth="lg">
            <Eyebrow>Who LawnHub is for</Eyebrow>
            <SectionTitle>One platform, two sides of the yard.</SectionTitle>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {AUDIENCES.map((a) => (
                <Grid item xs={12} md={6} key={a.to}>
                  <Box
                    component={RouterLink}
                    to={a.to}
                    sx={{
                      display: 'block',
                      height: '100%',
                      textDecoration: 'none',
                      bgcolor: '#fff',
                      border: `1px solid ${LINE}`,
                      borderRadius: 3,
                      p: { xs: 3, md: 4 },
                      transition: 'border-color 150ms ease, transform 150ms ease',
                      '&:hover': { borderColor: GREEN, transform: 'translateY(-2px)' },
                      '&:hover .lh-arrow': { transform: 'translateX(4px)' },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: GREEN, mb: 1.5 }}>
                      {a.eyebrow}
                    </Typography>
                    <Typography component="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.6rem' }, letterSpacing: '-0.02em', color: INK, mb: 1.5 }}>
                      {a.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.975rem', lineHeight: 1.65, color: MUTED, mb: 3 }}>{a.body}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: GREEN, fontWeight: 700 }}>
                      <span>{a.cta}</span>
                      <ArrowForward className="lh-arrow" sx={{ fontSize: 18, transition: 'transform 150ms ease' }} />
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <ClosingCta
          title="Put LawnHub to work this season."
          body="Create a provider account in a few minutes, or see everything that’s included first."
          primary={{ label: 'Sign up free as a provider', onClick: handleGetStarted }}
          secondary={{ label: 'See provider benefits', to: '/provider-benefits' }}
        />
      </MarketingPage>
    </>
  );
};

export default AboutUsPage;
